/**
 * API Error Handling Utilities
 * Provides centralized API error processing and logging
 * Enhanced to work with the unified error handling system from Phase 2
 * NO SENSITIVE DATA - Client-side only utilities
 */

/**
 * Process API error response into standardized format
 * @param {Error} error - Axios error object
 * @param {string} context - Context where error occurred (e.g., 'login', 'upload')
 * @returns {object} Standardized error object
 */
export const processApiError = (error, context = '') => {
  // Network/connection errors
  if (!error.response) {
    return {
      type: 'network',
      message: 'Network connection failed. Please check your internet connection.',
      code: 'NETWORK_ERROR',
      context,
      retryable: true
    };
  }

  // HTTP status-based error handling
  const status = error.response.status;
  const data = error.response.data;

  // Enhanced backend error format from Phase 2 (unified error handling)
  if (data?.success === false && data?.error) {
    return {
      type: getErrorType(status),
      message: data.error.message || 'An error occurred',
      code: data.error.code || getDefaultErrorCode(status),
      details: data.error.details || {},
      requestId: data.error.request_id,
      level: data.error.level, // ERROR, CRITICAL, WARNING, INFO from enhanced backend
      context,
      retryable: isRetryable(status, data.error.code),
      timestamp: new Date().toISOString()
    };
  }

  // Legacy backend structured error format (for backward compatibility)
  if (data?.error) {
    return {
      type: getErrorType(status),
      message: data.error.message || 'An error occurred',
      code: data.error.code || getDefaultErrorCode(status),
      details: data.error.details || {},
      requestId: data.error.request_id,
      context,
      retryable: isRetryable(status, data.error.code),
      timestamp: new Date().toISOString()
    };
  }

  // Fallback for non-structured errors
  return {
    type: getErrorType(status),
    message: getDefaultMessage(status, data),
    code: getDefaultErrorCode(status),
    context,
    retryable: isRetryable(status),
    timestamp: new Date().toISOString()
  };
};

/**
 * Determine error type based on HTTP status
 * @param {number} status - HTTP status code
 * @returns {string} Error type
 */
const getErrorType = (status) => {
  switch (status) {
    case 401:
      return 'authentication';
    case 403:
      return 'authorization';
    case 422:
      return 'validation';
    case 408:
    case 504:
      return 'timeout';
    case 429:
      return 'ratelimit';
    default:
      if (status >= 400 && status < 500) {
        return 'client';
      } else if (status >= 500) {
        return 'server';
      } else {
        return 'unknown';
      }
  }
};

/**
 * Get default error code based on status
 * @param {number} status - HTTP status code
 * @returns {string} Error code
 */
const getDefaultErrorCode = (status) => {
  const errorCodes = {
    400: 'BAD_REQUEST',
    401: 'AUTH_ERROR',
    403: 'PERMISSION_ERROR',
    404: 'NOT_FOUND',
    408: 'TIMEOUT_ERROR',
    422: 'VALIDATION_ERROR',
    429: 'RATE_LIMIT_ERROR',
    500: 'SERVER_ERROR',
    502: 'BAD_GATEWAY',
    503: 'SERVICE_UNAVAILABLE',
    504: 'GATEWAY_TIMEOUT'
  };
  
  return errorCodes[status] || `HTTP_${status}`;
};

/**
 * Get default error message based on status code
 * @param {number} status - HTTP status code
 * @param {object} data - Response data
 * @returns {string} Error message
 */
const getDefaultMessage = (status, data) => {
  const messages = {
    400: 'Invalid request. Please check your input.',
    401: 'Authentication required. Please login.',
    403: 'Access denied. You don\'t have permission.',
    404: 'Resource not found.',
    408: 'Request timeout. Please try again.',
    422: 'Invalid data provided.',
    429: 'Too many requests. Please wait and try again.',
    500: 'Server error. Please try again later.',
    502: 'Service temporarily unavailable.',
    503: 'Service maintenance in progress.',
    504: 'Request timeout. Please try again.'
  };

  // Try to extract message from response
  if (data?.message) {
    return data.message;
  }

  return messages[status] || `Request failed with status ${status}`;
};

/**
 * Determine if error is retryable
 * @param {number} status - HTTP status code
 * @param {string} errorCode - Backend error code
 * @returns {boolean} Whether error is retryable
 */
const isRetryable = (status, errorCode) => {
  // Non-retryable client errors
  if (status === 400 || status === 401 || status === 403 || status === 404 || status === 422) {
    return false;
  }

  // Specific error codes that shouldn't be retried
  const nonRetryableCodes = [
    'AUTH_INVALID_CREDENTIALS',
    'VALIDATION_INVALID_FORMAT',
    'FILE_INVALID_FORMAT'
  ];

  if (errorCode && nonRetryableCodes.includes(errorCode)) {
    return false;
  }

  // Server errors and timeouts are generally retryable
  return status >= 500 || status === 408 || status === 429;
};

/**
 * Check if error indicates authentication failure
 * @param {object} error - Processed error object
 * @returns {boolean} Whether error is auth-related
 */
export const isAuthError = (error) => {
  return error.code?.startsWith('AUTH_') || 
         error.type === 'client' && error.message?.toLowerCase().includes('authentication');
};

/**
 * Check if error indicates validation failure
 * @param {object} error - Processed error object
 * @returns {boolean} Whether error is validation-related
 */
export const isValidationError = (error) => {
  return error.code?.startsWith('VAL_') || 
         error.code?.startsWith('VALIDATION_') ||
         (error.type === 'client' && error.message?.toLowerCase().includes('validation'));
};

/**
 * Get user-friendly error message for display
 * @param {object} error - Processed error object
 * @param {string} fallback - Fallback message
 * @returns {string} User-friendly message
 */
export const getUserMessage = (error, fallback = 'An error occurred') => {
  if (!error) return fallback;
  
  // Return the message, but sanitize it for display
  const message = error.message || fallback;
  
  // Remove any potential sensitive information patterns
  // (This is client-side sanitization for extra safety)
  return message
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[email]')
    .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[ip]')
    .replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '[uuid]');
};

/**
 * Log error for debugging (development only, no sensitive data)
 * @param {object} error - Processed error object
 * @param {object} context - Additional context
 */
export const logError = (error, context = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  console.error('[API Error]', {
    type: error.type,
    code: error.code,
    message: error.message,
    context: error.context,
    requestId: error.requestId,
    retryable: error.retryable,
    timestamp: new Date().toISOString(),
    additionalContext: context
  });
};

/**
 * Extract error information from response data
 * @param {object} errorResponse - Error response object
 * @returns {object} Extracted error information
 */
export const extractErrorInfo = (errorResponse) => {
  if (!errorResponse) {
    return {
      message: 'Unknown error occurred',
      code: 'UNKNOWN_ERROR',
      details: {}
    };
  }

  // Handle direct error string
  if (typeof errorResponse === 'string') {
    return {
      message: errorResponse,
      code: 'STRING_ERROR',
      details: {}
    };
  }

  // Handle data object with error property  
  if (errorResponse.data?.error) {
    return {
      message: errorResponse.data.error,
      code: 'DATA_ERROR',
      details: errorResponse.data.details || {}
    };
  }

  // Handle direct error object
  if (errorResponse.error) {
    return {
      message: errorResponse.error.message || errorResponse.error,
      code: errorResponse.error.code || 'API_ERROR',
      details: errorResponse.error.details || {},
      requestId: errorResponse.error.request_id
    };
  }

  // Handle response with nested error
  if (errorResponse.response?.data?.error) {
    const error = errorResponse.response.data.error;
    return {
      message: error.message || error,
      code: error.code || 'API_ERROR',
      details: error.details || {},
      requestId: error.request_id
    };
  }

  // Fallback for simple data structure
  return {
    message: errorResponse.message || 'An error occurred',
    code: 'EXTRACTION_ERROR',
    details: { originalResponse: errorResponse }
  };
};

/**
 * Handle network errors with proper formatting
 * @param {Error} networkError - Network error object
 * @returns {object} Formatted network error
 */
export const handleNetworkError = (networkError) => {
  return {
    type: 'network',
    message: 'Network connection failed. Please check your internet connection and try again.',
    code: networkError.code || 'NETWORK_ERROR',
    details: {
      originalMessage: networkError.message,
      name: networkError.name,
      isNetworkError: true
    },
    retryable: true,
    timestamp: new Date().toISOString()
  };
};

/**
 * Format error message for display
 * @param {object} error - Error object 
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  let message = error.message || 'An error occurred';
  
  // Add error code if available
  if (error.code) {
    message += ` (${error.code})`;
  }
  
  // Add request ID for tracking if available
  if (error.requestId) {
    message += ` [Request: ${error.requestId}]`;
  }
  
  return message;
};

/**
 * Extract error details for debugging
 * @param {object} error - Error object
 * @returns {object} Error details
 */
export const extractErrorDetails = (error) => {
  return {
    type: error.type || 'unknown',
    code: error.code || 'UNKNOWN',
    message: error.message || 'No message available',
    details: error.details || {},
    requestId: error.requestId || null,
    timestamp: error.timestamp || new Date().toISOString(),
    retryable: error.retryable || false
  };
};